import { useState } from 'react';
import { useSession } from '../auth/session.tsx';
import { formatDate } from '../dates.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { formatMoneyText, lastFour } from '../money.ts';
import { blockingWords } from '../ledgerImportWords.ts';
import {
  commitImport, mapImportCategory, previewImport,
  type ImportCounts, type ImportPreview, type LedgerAccount, type LedgerCategory, type PreviewLine,
} from '../api/ledger.ts';

/**
 * IMPORT FROM QUICKEN (money phase 4, screen C; slice 3's routes). AP/AR and tier 1 only: drawn for them, and every
 * route refuses anyone else. The file is read in the browser and held in THIS component's state between preview and
 * commit; it is never uploaded to Documents, stored or logged. The preview's answer, the Quicken category text among
 * it (Q8, to the importer only), lives here too and never in the query cache: leaving the panel forgets it.
 * Steps: choose the account and the file; PREVIEW (nothing written); MAP each category Procura does not know (by its
 * hash; the commit is refused while any is unmapped, A-#348); COMMIT the same file with the preview's sha256, all or
 * nothing.
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

/** The file's bytes as base64, read in the browser: the body the preview and the commit carry. */
async function fileBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

const STATE_WORDS: Record<PreviewLine['state'], string> = {
  'new': 'New', 'already imported': 'Already imported', 'conflict': 'Conflict', 'refused': 'Refused', 'held': 'Held',
};

export function LedgerImport({ clientId, accounts, categories, onDone }: {
  clientId: string; accounts: LedgerAccount[]; categories: LedgerCategory[]; onDone: (m: string | null) => void;
}) {
  const { getToken } = useSession();
  const open = accounts.filter((a) => !a.withdrawn);
  const [accountId, setAccountId] = useState(open[0]?.id ?? '');
  const [older, setOlder] = useState(false);
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [section, setSection] = useState<number | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [notDuplicates, setNotDuplicates] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const ask = async (sec: number | null = section) => {
    if (!file || !accountId) return;
    setBusy(true); setError(null); setSaid(null);
    try {
      const p = await previewImport(clientId, { accountId, file: file.base64, olderQuicken: older, section: sec }, getToken);
      setPreview(p);
      setNotDuplicates(new Set());
    } catch (e) { setError(e as Error); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file || !preview || preview.needsSection) return;
    const left = blockingWords(preview.blocking, notDuplicates.size);
    if (left.length > 0) { setSaid(left.join('. ') + '.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await commitImport(clientId, { accountId, file: file.base64, olderQuicken: older, section: preview.section,
        sha256: preview.sha256, notDuplicates: [...notDuplicates] }, getToken);
      // the file and the preview are forgotten the moment the import is written
      setFile(null); setPreview(null);
      onDone(`Imported: ${r.counts.new} new, ${r.counts.unchanged} already there.`);
    } catch (e) { setError(e as Error); } finally { setBusy(false); }
  };

  const map = async (u: { hash: string; version: string }) => {
    const category = mapping[u.hash];
    if (!category) return;
    setBusy(true); setError(null);
    try {
      await mapImportCategory({ hash: u.hash, version: u.version, category }, getToken);
      await ask();
    } catch (e) { setError(e as Error); } finally { setBusy(false); }
  };

  const ready = preview && !preview.needsSection ? preview : null;
  // each category Procura does not know, once, with Quicken's own words: shown here only (Q8)
  const unmapped = ready ? [...new Map(ready.lines.flatMap((l) => l.unmapped).map((u) => [u.hash, u])).values()] : [];
  const usable = categories.filter((c) => c.active && !c.settles_advances);
  const id = `import-${clientId}`;

  return (
    <section aria-labelledby={`${id}-title`} className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3">
      <h3 id={`${id}-title`} className="font-semibold text-neutral-900">Import from Quicken</h3>
      <p className={hint}>A Quicken export (QIF). Nothing is written until you commit, and the file itself is never kept.</p>
      <div>
        <label className={label} htmlFor={`${id}-account`}>Into the account</label>
        <select id={`${id}-account`} className={field} value={accountId}
                onChange={(e) => { setAccountId(e.target.value); setPreview(null); }}>
          {open.map((a) => <option key={a.id} value={a.id}>{a.institution_name} {lastFour(a.last4)}</option>)}
        </select>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-file`}>The file</label>
        <input id={`${id}-file`} type="file" accept=".qif,.QIF,text/plain" className={field}
               onChange={(e) => {
                 const f = e.target.files?.[0];
                 setPreview(null); setSection(null);
                 if (!f) { setFile(null); return; }
                 void fileBase64(f).then((b) => setFile({ name: f.name, base64: b }));
               }} />
      </div>
      <label className="flex min-h-[44px] items-center gap-2">
        <input type="checkbox" checked={older} onChange={(e) => { setOlder(e.target.checked); setPreview(null); }} />
        An older Quicken (the file is not UTF-8)
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={primary} disabled={!file || !accountId || busy} onClick={() => void ask()}>
          {busy ? 'Reading…' : 'Preview'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Close</button>
      </div>
      {error && <Refusal error={error} />}

      {preview?.needsSection && (
        <fieldset>
          <legend className={label}>The file holds more than one account: which is this one?</legend>
          {preview.sections.map((s) => (
            <label key={s.place} className="flex min-h-[44px] items-center gap-2">
              <input type="radio" name={`${id}-section`} checked={section === s.place}
                     onChange={() => { setSection(s.place); void ask(s.place); }} />
              {s.account || 'An account'}: {s.transactions} transactions
              {s.from ? `, ${formatDate(s.from)} to ${s.to ? formatDate(s.to) : '?'}` : ''}
            </label>
          ))}
        </fieldset>
      )}

      {ready && (
        <div className="space-y-3">
          <ImportCountsLine counts={ready.counts} />
          {unmapped.length > 0 && (
            <div className="rounded-lg border border-neutral-300 p-3">
              <h4 className="font-semibold text-neutral-900">Categories Procura does not know</h4>
              <p className={hint}>Quicken's words are shown here only, for this import; they are never kept. Map each to a category.</p>
              <ul className="mt-2 space-y-2">
                {unmapped.map((u) => (
                  <li key={u.hash} className="flex flex-wrap items-end gap-2">
                    <span className="min-w-[12rem] text-neutral-900">{u.text}</span>
                    <label className="sr-only" htmlFor={`${id}-map-${u.hash.slice(0, 12)}`}>Its category</label>
                    <select id={`${id}-map-${u.hash.slice(0, 12)}`} className={field} value={mapping[u.hash] ?? ''}
                            onChange={(e) => setMapping((m) => ({ ...m, [u.hash]: e.target.value }))}>
                      <option value="">Choose…</option>
                      {usable.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <button type="button" className={secondary} disabled={!mapping[u.hash] || busy} onClick={() => void map(u)}>Map</button>
                  </li>
                ))}
              </ul>
              <p className={hint}>A category Procura lacks is added first, by the Director of Finance or tier 1, and the file previewed again.</p>
            </div>
          )}
          <ol className="divide-y divide-neutral-100 rounded-lg border border-neutral-300">
            {ready.lines.map((l) => (
              <li key={l.ordinal} className="px-3 py-2 text-sm">
                <p className="flex flex-wrap gap-x-3 text-neutral-900">
                  <span className="font-semibold">{STATE_WORDS[l.state]}</span>
                  <span className="tabular-nums">{l.date ? formatDate(l.date) : 'No date'}</span>
                  <span className="tabular-nums">{formatMoneyText(l.amount)}</span>
                  <span>{l.payee ?? 'No payee'}</span>
                  {l.transfer ? <span>Transfer {l.transfer}</span> : l.category_code ? <span>{l.category_code}</span> : null}
                </p>
                {l.memo && <p className="text-neutral-700">{l.memo}</p>}
                {l.words.map((w) => <p key={w} className="text-neutral-700">{w}</p>)}
                {l.duplicate_of_other_source && (l.key ? (
                  <label className="flex min-h-[44px] items-center gap-2">
                    <input type="checkbox" checked={notDuplicates.has(l.key)}
                           onChange={(e) => setNotDuplicates((s) => {
                             const n = new Set(s); if (e.target.checked) n.add(l.key!); else n.delete(l.key!); return n;
                           })} />
                    Not a duplicate: import it
                  </label>
                ) : (
                  <p className="text-neutral-700">Ask AP/AR's lead: this line cannot be cleared here yet.</p>
                ))}
              </li>
            ))}
          </ol>
          {said && <p role="status" className="text-sm font-semibold text-neutral-900">{said}</p>}
          <button type="button" className={primary} disabled={busy} onClick={() => void commit()}>Commit the import</button>
        </div>
      )}
    </section>
  );
}

function ImportCountsLine({ counts: c }: { counts: ImportCounts }) {
  return (
    <p className="text-sm text-neutral-800">
      {c.lines} {c.lines === 1 ? 'line' : 'lines'}: {c.new} new, {c.unchanged} already imported, {c.conflicts} in conflict,
      {' '}{c.refused} refused, {c.held} held, {c.duplicates} possible duplicates, {c.unmapped} with an unknown category.
      {c.masked_memos > 0 && ` ${c.masked_memos} ${c.masked_memos === 1 ? 'memo had a long number' : 'memos had a long number'} shortened to its last four.`}
      {c.transfers_paired + c.transfers_awaiting > 0 && ` Transfers: ${c.transfers_paired} paired, ${c.transfers_awaiting} awaiting their other half.`}
    </p>
  );
}
